/**
 * Multi-resolution waveform peak pyramid (mipmap for audio).
 *
 * Naive peak extraction walks every sample in the visible range on every
 * render — O(range) per frame, which stalls at high zoom on long files and
 * defeats caching (each zoom step is a new range).
 *
 * Instead we build min/max buckets ONCE per AudioBuffer at geometrically
 * increasing resolutions. Rendering then reads whichever level is closest to
 * the current samples-per-pixel, so drawing cost is O(visible pixels) no
 * matter how long the file is or how far you zoom out.
 *
 * Memory is bounded: the base level is length/BASE_BUCKET entries and each
 * level above is a quarter of the one below, summing to ~1.33x the base.
 */

import { buildWaveformPeaks } from './samplePreparationClient';

const BASE_BUCKET = 256; // samples per bucket at the finest pyramid level
const LEVEL_RATIO = 4; // each level covers 4x the samples of the one below
const MAX_LEVELS = 12;

const pyramidCache = new WeakMap();
const pyramidPromiseCache = new WeakMap();

const pyramidFromBase = (base, length, sampleRate, baseBucket = BASE_BUCKET) => {
  const levels = [{ bucketSize: baseBucket, data: base }];

  while (
    levels.length < MAX_LEVELS
    && levels[levels.length - 1].data.length / 2 > 1
  ) {
    const prev = levels[levels.length - 1];
    const prevBuckets = prev.data.length / 2;
    const nextBuckets = Math.ceil(prevBuckets / LEVEL_RATIO);
    const next = new Float32Array(nextBuckets * 2);
    for (let b = 0; b < nextBuckets; b += 1) {
      const start = b * LEVEL_RATIO;
      const end = Math.min(prevBuckets, start + LEVEL_RATIO);
      let min = Infinity;
      let max = -Infinity;
      for (let i = start; i < end; i += 1) {
        const lo = prev.data[i * 2];
        const hi = prev.data[(i * 2) + 1];
        if (lo < min) min = lo;
        if (hi > max) max = hi;
      }
      if (min === Infinity) { min = 0; max = 0; }
      next[b * 2] = min;
      next[(b * 2) + 1] = max;
    }
    levels.push({ bucketSize: prev.bucketSize * LEVEL_RATIO, data: next });
  }

  return { levels, length, sampleRate };
};

export const getCachedPeakPyramid = (audioBuffer) => (
  audioBuffer ? pyramidCache.get(audioBuffer) || null : null
);

/** Build the expensive base waveform index in the Rust preparation Worker. */
export function preparePeakPyramid(audioBuffer) {
  if (!audioBuffer || !audioBuffer.length) return Promise.resolve(null);
  const cached = pyramidCache.get(audioBuffer);
  if (cached) return Promise.resolve(cached);
  const pending = pyramidPromiseCache.get(audioBuffer);
  if (pending) return pending;

  const channels = Array.from(
    { length: Math.max(1, audioBuffer.numberOfChannels || 1) },
    (_, channel) => audioBuffer.getChannelData(channel)
  );
  const promise = buildWaveformPeaks(channels, {
    sampleRate: audioBuffer.sampleRate,
    bucketSize: BASE_BUCKET
  }).then((result) => {
    const pyramid = pyramidFromBase(
      result.peaks,
      result.length || audioBuffer.length,
      result.sampleRate || audioBuffer.sampleRate,
      result.bucketSize || BASE_BUCKET
    );
    pyramidCache.set(audioBuffer, pyramid);
    pyramidPromiseCache.delete(audioBuffer);
    return pyramid;
  }).catch(() => {
    pyramidPromiseCache.delete(audioBuffer);
    // Browsers without Worker/WASM support keep the established synchronous
    // implementation as an audible and visual compatibility fallback.
    return getPeakPyramid(audioBuffer);
  });
  pyramidPromiseCache.set(audioBuffer, promise);
  return promise;
}

/**
 * Build (or fetch the cached) pyramid for a buffer.
 * Channels are folded together — a bucket's min/max spans all channels, which
 * matches how a DAW draws a summed clip envelope.
 */
export function getPeakPyramid(audioBuffer) {
  if (!audioBuffer || !audioBuffer.length) return null;
  const cached = pyramidCache.get(audioBuffer);
  if (cached) return cached;

  const length = audioBuffer.length;
  const channels = Math.max(1, audioBuffer.numberOfChannels || 1);
  const channelData = [];
  for (let c = 0; c < channels; c += 1) {
    channelData.push(audioBuffer.getChannelData(c));
  }

  // Base level straight from samples.
  const baseBuckets = Math.max(1, Math.ceil(length / BASE_BUCKET));
  const base = new Float32Array(baseBuckets * 2);
  for (let b = 0; b < baseBuckets; b += 1) {
    const start = b * BASE_BUCKET;
    const end = Math.min(length, start + BASE_BUCKET);
    let min = Infinity;
    let max = -Infinity;
    for (let c = 0; c < channels; c += 1) {
      const data = channelData[c];
      for (let i = start; i < end; i += 1) {
        const v = data[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    if (min === Infinity) { min = 0; max = 0; }
    base[b * 2] = min;
    base[(b * 2) + 1] = max;
  }

  const pyramid = pyramidFromBase(base, length, audioBuffer.sampleRate);
  pyramidCache.set(audioBuffer, pyramid);
  return pyramid;
}

/**
 * Pick the finest level whose buckets are no larger than one pixel's worth of
 * samples, so each pixel aggregates >= 1 bucket (never smears a bucket across
 * pixels). Falls back to the base level when zoomed in past it.
 */
export function selectLevel(pyramid, samplesPerPixel) {
  if (!pyramid || !pyramid.levels.length) return null;
  let chosen = pyramid.levels[0];
  for (let i = 0; i < pyramid.levels.length; i += 1) {
    if (pyramid.levels[i].bucketSize <= samplesPerPixel) chosen = pyramid.levels[i];
    else break;
  }
  return chosen;
}

/**
 * True when the view is zoomed in far enough that individual samples span a
 * pixel or more — the caller should draw actual sample points instead of
 * min/max bars.
 */
export function shouldRenderSamples(samplesPerPixel) {
  return samplesPerPixel < 1;
}

/**
 * Interleaved [min,max] pairs for `width` columns covering [startSample,
 * endSample). Reads from the pyramid, so cost scales with width, not range.
 */
export function getPeaksForRange(pyramid, startSample, endSample, width) {
  const cols = Math.max(1, Math.floor(width || 1));
  const out = new Float32Array(cols * 2);
  if (!pyramid) return out;

  const total = pyramid.length;
  const start = Math.max(0, Math.min(total, Math.floor(startSample || 0)));
  const end = Math.max(start + 1, Math.min(total, Math.floor(endSample ?? total)));
  const samplesPerPixel = (end - start) / cols;
  const level = selectLevel(pyramid, samplesPerPixel);
  if (!level) return out;

  const buckets = level.data.length / 2;

  for (let x = 0; x < cols; x += 1) {
    const colStart = start + (x * samplesPerPixel);
    const colEnd = colStart + samplesPerPixel;
    let bStart = Math.floor(colStart / level.bucketSize);
    let bEnd = Math.ceil(colEnd / level.bucketSize);
    bStart = Math.max(0, Math.min(buckets - 1, bStart));
    bEnd = Math.max(bStart + 1, Math.min(buckets, bEnd));

    let min = Infinity;
    let max = -Infinity;
    for (let b = bStart; b < bEnd; b += 1) {
      const lo = level.data[b * 2];
      const hi = level.data[(b * 2) + 1];
      if (lo < min) min = lo;
      if (hi > max) max = hi;
    }
    if (min === Infinity) { min = 0; max = 0; }
    out[x * 2] = min;
    out[(x * 2) + 1] = max;
  }

  return out;
}
